/**
 * Native-feel haptic feedback wrappers — the ONE TRUE source. Previously
 * duplicated in `apps/{customer-pwa,dashboard,agent-pwa}/src/lib/native-feel/`;
 * extracted to `@kilat/ui` in the unification sprint so all callers share
 * one implementation.
 *
 * Per [[feedback-native-pwa-ux]] and [[feedback-dopamine-for-all]].
 * Three semantic intensities mapped onto `navigator.vibrate` patterns so
 * call sites don't reason about ms.
 *
 * ## iOS delivers nothing, and pretending otherwise was the bug (G27)
 *
 * `navigator.vibrate` does not exist in iOS Safari — not disabled, not
 * permission-gated, absent. Apple exposes no web vibration API at all, and no
 * amount of engineering changes that. So on an iPhone every `feel()` call was
 * a silent no-op: no buzz, and sound is opt-in and defaults off, so nothing.
 * A large share of the Malaysian market got a product that never once
 * acknowledged a tap.
 *
 * What can honestly be done is give those devices a DIFFERENT channel rather
 * than a fake one:
 *
 *   - `hapticsAvailable()` lets a settings screen say why sound matters more
 *     on this device instead of offering a toggle that does nothing.
 *   - `iosFallback()` pulses the element the user actually pressed. It is
 *     visual, it is local to the touch, and it is the only acknowledgement
 *     available when vibration is absent and sound is off.
 *
 * Both respect `prefers-reduced-motion: reduce`.
 */

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * Whether this device can vibrate at all.
 *
 * Exported so a UI can be honest about it: on iOS the answer is permanently
 * false, and a settings screen should explain that sound is the feedback
 * channel here rather than showing a haptics toggle that cannot work.
 */
export function hapticsAvailable(): boolean {
  return typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function';
}

function vibrate(pattern: number | number[]): void {
  if (!hapticsAvailable()) return;
  if (prefersReducedMotion()) return;
  try {
    navigator.vibrate(pattern);
  } catch {
    /* swallow — some embedded browsers throw on Vibration access */
  }
}

/** How long each intensity's visual pulse lasts, in ms. */
const PULSE_MS: Record<'tap' | 'success' | 'celebration', number> = {
  tap: 140,
  success: 260,
  celebration: 420,
};

/**
 * Visual acknowledgement for devices with no vibration.
 *
 * Pulses the pressed element itself rather than flashing the screen: the
 * feedback belongs where the finger is, and a full-screen effect on every tap
 * would be intolerable by the third one.
 *
 * Driven by a data attribute so the animation lives in `premium-motion.css`
 * with the rest of the motion vocabulary, and inherits the reduced-motion
 * handling already defined there.
 *
 * No-op when the element is missing, when vibration IS available (the buzz is
 * the better signal), or under reduced motion.
 */
export function iosFallback(
  el: Element | null | undefined,
  kind: 'tap' | 'success' | 'celebration' = 'tap',
): void {
  if (!el || hapticsAvailable() || prefersReducedMotion()) return;
  if (typeof window === 'undefined') return;

  const node = el as HTMLElement;
  try {
    // Restart a pulse already in flight: a fast double-tap should read as two
    // acknowledgements, not one that swallows the second.
    node.removeAttribute('data-feel-pulse');
    void node.offsetWidth;
    node.setAttribute('data-feel-pulse', kind);
    window.setTimeout(() => node.removeAttribute('data-feel-pulse'), PULSE_MS[kind]);
  } catch {
    /* a detached or exotic node — feedback is never worth throwing over */
  }
}

/**
 * The element the user last pressed, and when.
 *
 * Most `feel()` call sites are `onClick={() => { feel('tap'); … }}` — no event
 * in scope, so no element to pulse. Rewriting some seventy handlers to thread
 * one through would be a large mechanical change for a small feature, and every
 * new handler would have to remember.
 *
 * A single passive capture-phase listener records the press instead, so
 * `feel()` can find the element on its own. The freshness window matters: a
 * success `feel()` fired from a network callback seconds later must NOT pulse
 * whatever the user happened to touch before — that would put feedback
 * somewhere they are no longer looking.
 */
let lastPressed: { el: Element; at: number } | null = null;
let listening = false;

/** How recently a press must have happened for it to own the feedback. */
const PRESS_FRESHNESS_MS = 1_000;

function watchPresses(): void {
  if (listening || typeof window === 'undefined' || !window.addEventListener) return;
  listening = true;
  window.addEventListener(
    'pointerdown',
    (e) => {
      const t = e.target;
      if (t instanceof Element) lastPressed = { el: t, at: Date.now() };
    },
    // Capture so it still records when a handler stops propagation; passive so
    // it can never delay a tap.
    { capture: true, passive: true },
  );
}

/** The pressed element, if the press was recent enough to still own feedback. */
export function recentlyPressedElement(): Element | null {
  watchPresses();
  if (!lastPressed) return null;
  return Date.now() - lastPressed.at <= PRESS_FRESHNESS_MS ? lastPressed.el : null;
}

export const haptic = {
  /** A single short tap — for primary CTA presses. */
  tap(): void {
    vibrate(8);
  },
  /** A confirmation — for successful saves, status advances, etc. */
  success(): void {
    vibrate([12, 30, 12]);
  },
  /** Celebration for milestones — paired with confetti, used sparingly. */
  celebration(): void {
    vibrate([20, 40, 20, 40, 80]);
  },
};
